import React, { useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import PQueue from 'p-queue';
import { useDispatch } from 'react-redux';

import { enqueueSnackbar } from '../../actions/applicationMessageActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import EsmsDialog from '../../components/EsmsDialog';
import FormControl from '@material-ui/core/FormControl';
import LinearProgress from '@material-ui/core/LinearProgress';
import SampleIntervalSelect from '../../components/FormFields/SampleIntervalSelect';
import { putProfileObject } from '../../actions/profileObjectActions';
import useProfileObjects from '../../hooks/useProfileObjects';

import classes from './ChangeTrendIntervalModal.module.css';

const ChangeTrendIntervalModal = ({ onClose, open, profile }) => {
    const dispatch = useDispatch();
    const [completed, setCompletedRequests] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestErrors, setRequestErrors] = useState([]);
    const [sampleInterval, setSampleInterval] = useState('');
    const [pending, setPendingRequests] = useState(0);
    const {
        errors: profileObjectErrors,
        loading: loadingProfileObjects,
        profileObjects
    } = useProfileObjects(profile && profile.id);

    function handleClose() {
        setCompletedRequests(0);
        setPendingRequests(0);
        setSampleInterval('');
        setRequestErrors([]);
        onClose();
    }

    async function handleConfirm() {
        const queue = new PQueue({ concurrency: 4 });
        const results = [];
        setIsSubmitting(true);
        for (let profileObject of profileObjects) {
            await queue.add(async () => {
                try {
                    await dispatch(putProfileObject({
                        ...profileObject,
                        sampleInterval
                    }));
                    results.push({
                        item:   profileObject,
                        status: 'fulfilled',
                        value:  profileObject
                    });
                } catch (error) {
                    results.push({
                        item:   profileObject,
                        status: 'rejected',
                        error
                    });
                } finally {
                    setCompletedRequests(prevCompleted => prevCompleted + 1);
                }
            });
        }
        queue.on('active', () => {
            const pending = queue.pending;
            setPendingRequests(pending);
        });
        await queue.onIdle();
        const resultErrors = results.filter(
            ({ status }) => status === 'rejected'
        );
        setIsSubmitting(false);
        setRequestErrors(resultErrors);
        if (!resultErrors.length) {
            dispatch(
                enqueueSnackbar(
                    'Trend Interval changed successfully',
                    'success'
                )
            );
            handleClose();
        }
    }

    function onChange(event) {
        setSampleInterval(event.target.value);
    }

    let content = null;
    if (loadingProfileObjects) {
        content = (
            <div className={`${classes.progress} ${classes.circularProgress}`}>
                <CircularProgress />
                <p>Loading Profile Objects...</p>
            </div>
        );
    } else if (isSubmitting) {
        const percentCompleted = completed / profileObjects.length * 100;
        const percentPending = (completed + pending) / profileObjects.length * 100;
        content = (
            <div className={`${classes.progress} ${classes.linearProgress}`}>
                <LinearProgress
                    value={percentCompleted}
                    valueBuffer={percentPending}
                    variant="buffer"
                />
                <p>Updating Profile Objects...</p>
            </div>
        );
    } else {
        content = (
            <form className={classes.form}>
                <FormControl>
                    <SampleIntervalSelect
                        onChange={onChange}
                        value={sampleInterval}
                    />
                </FormControl>
            </form>
        );
    }

    const disabled =
        loadingProfileObjects || isSubmitting || sampleInterval === '';
    const errors = [...requestErrors, ...profileObjectErrors];

    return (
        <EsmsDialog
            confirmText="Change"
            disabled={disabled}
            errors={errors}
            imgSrc="/assets/images/profilesIconColor.svg"
            onConfirm={handleConfirm}
            onClose={handleClose}
            open={open}
            title="Change Trend Interval"
        >
            {content}
        </EsmsDialog>
    );
};

ChangeTrendIntervalModal.defaultProps = {
    onClose: () => {},
    open:    false,
    profile: {}
};

ChangeTrendIntervalModal.propTypes = {
    onClose: func,
    open:    bool,
    profile: shape({
        id: string
    })
};

export default ChangeTrendIntervalModal;
